import { useState, useRef, useEffect } from "react";
import { Download, Share2, Instagram, Twitter, Facebook, MessageCircle, Copy, ChevronDown } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Share/Download dropdown for a single artwork.
 *
 * Buttons:
 *  - Download PDF flyer
 *  - Download share image (square / story / landscape, PNG)
 *  - Open native Web Share sheet (mobile)
 *  - Direct intent links: X (Twitter), Facebook, WhatsApp
 *  - Copy direct artwork URL
 *
 * Instagram does not allow direct sharing of an arbitrary asset via URL, so we
 * tell the user to download the image and post manually (industry-standard
 * workaround used by every art marketplace).
 */
export function ArtworkShareMenu({ artwork }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const artworkUrl = `https://africaartmasters.com/artwork/${artwork.id}`;
  const shareText = `${artwork.title} by ${artwork.artist_name} · AfricaArtMasters`;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const img = (size, fmt = "png") =>
    `${BACKEND_URL}/api/flyer/artwork/${artwork.id}/image?fmt=${fmt}&size=${size}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(artworkUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
    setOpen(false);
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({ title: artwork.title, text: shareText, url: artworkUrl });
    } catch (err) {
      // User cancelled — silent
      if (err.name !== "AbortError") toast.error("Could not open share");
    }
    setOpen(false);
  };

  const instagramFlow = () => {
    // IG has no shareable web intent; download the square share card so the
    // artist/collector can post it manually.
    const a = document.createElement("a");
    a.href = img("square", "png");
    a.download = `aam-${artwork.id}-square.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Square image downloaded — post it to Instagram");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="artwork-share-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="artwork-share-trigger"
        className="w-full inline-flex items-center justify-center gap-2 border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors py-4 label-eyebrow"
      >
        <Share2 className="w-3.5 h-3.5" strokeWidth={2} />
        Share &amp; Download
        <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="artwork-share-dropdown"
          className="absolute right-0 mt-2 w-72 bg-white border border-[#E5E0D8] shadow-lg z-30"
        >
          <div className="p-3 border-b border-[#E5E0D8]">
            <p className="label-eyebrow text-[#555]">— Downloads</p>
            <a
              href={`${BACKEND_URL}/api/flyer/artwork/${artwork.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dl-pdf"
              onClick={() => setOpen(false)}
              className="mt-2 flex items-center gap-2 px-2 py-2 hover:bg-[#F4EFE6]/60 text-sm text-[#111]"
            >
              <Download className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} /> Flyer (PDF)
            </a>
            <a
              href={img("square")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dl-square"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-2 py-2 hover:bg-[#F4EFE6]/60 text-sm text-[#111]"
            >
              <Download className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} /> Square (PNG — IG feed)
            </a>
            <a
              href={img("story")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dl-story"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-2 py-2 hover:bg-[#F4EFE6]/60 text-sm text-[#111]"
            >
              <Download className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} /> Story (PNG — IG/WhatsApp)
            </a>
            <a
              href={img("landscape", "jpg")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dl-landscape"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-2 py-2 hover:bg-[#F4EFE6]/60 text-sm text-[#111]"
            >
              <Download className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} /> Landscape (JPG — X/Facebook)
            </a>
          </div>

          <div className="p-3">
            <p className="label-eyebrow text-[#555]">— Share to</p>
            <div className="mt-2 grid grid-cols-4 gap-2">
              <button
                onClick={instagramFlow}
                data-testid="share-instagram"
                aria-label="Instagram"
                className="aspect-square flex items-center justify-center border border-[#E5E0D8] hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(artworkUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="share-twitter"
                aria-label="X (Twitter)"
                className="aspect-square flex items-center justify-center border border-[#E5E0D8] hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
              >
                <Twitter className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(artworkUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="share-facebook"
                aria-label="Facebook"
                className="aspect-square flex items-center justify-center border border-[#E5E0D8] hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${artworkUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="share-whatsapp"
                aria-label="WhatsApp"
                className="aspect-square flex items-center justify-center border border-[#E5E0D8] hover:bg-[#1E3A8A] hover:text-white hover:border-[#1E3A8A] transition-colors"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={nativeShare}
                data-testid="share-native"
                className="label-eyebrow border border-[#E5E0D8] hover:bg-[#F4EFE6]/60 px-3 py-2 text-[#1E3A8A] text-[10px]"
              >
                More…
              </button>
              <button
                onClick={copyLink}
                data-testid="share-copy"
                className="label-eyebrow border border-[#E5E0D8] hover:bg-[#F4EFE6]/60 px-3 py-2 text-[#1E3A8A] text-[10px] inline-flex items-center justify-center gap-1"
              >
                <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArtworkShareMenu;
